import { useApp } from '@/App';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';
import fetcher from '@/libs/fleet-api/fetcher';
import { getRequestSurveyDetail } from '@/libs/fleet-api/services/prospect';
import {
	submitFBDHApprovalSurvey,
	SubmitFBDHApprovalSurveyPayload,
} from '@/libs/fleet-api/services/survey';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Divider,
	Form,
	Image,
	Input,
	notification,
	Row,
	Select,
	Table,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

export type FBDHRequestSurveyPageProps = {
	prospectId: string;
};

const FBDHRequestSurveyPage: FC<FBDHRequestSurveyPageProps> = ({
	prospectId,
}) => {
	const { navigate } = useApp();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { form, fieldName } = useForm<SubmitFBDHApprovalSurveyPayload>();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openPraSurveyDataModal,
		openCustomerCheckingModal,
		openProspectDetail,
		openDocumentModal,
		openMapModal,
	} = useModals();

	const { data, isLoading, isError } = useQuery({
		queryKey: ['getRequestSurveyDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getRequestSurveyDetail(id);
		},
	});

	const submitFBDHApprovalSurveyMutation = useMutation({
		mutationFn: submitFBDHApprovalSurvey,
	});

	const {
		approvalHistories,
		prospectInformation,
		mainInformation,
		surveyInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
	} = data?.data || {};

	const surveyParticipants = surveyInformation?.surveyParticipants ?? [];

	// TODO: Set Real Value
	const customerInfoDataRow: DataRowItem[] = [
		[
			[
				'Nama Customer',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerDetail(mainInformation!.customerId);
					}}
				>
					{mainInformation?.customerName}
				</a>,
			],
			[
				'Prospect ID',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openProspectDetail(mainInformation!.prospectId);
					}}
				>
					{mainInformation?.prospectId}
				</a>,
			],
		],
		[
			['Kelas Industri', mainInformation?.industrySector?.industryClass],
			['Sumber Order', mainInformation?.orderSource],
		],
		[
			[
				'Portofolio Guideline',
				mainInformation?.industrySector?.portofolioGuideline,
			],
			[
				'Group Customer BMPP',
				mainInformation?.customerGroup?.id ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openCustomerGroupDetail(mainInformation!.customerGroup.id);
						}}
					>
						{mainInformation?.customerGroup.value}
					</a>
				) : (
					'-'
				),
			],
		],
		[
			[
				'Data Pra-Survey',
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openPraSurveyDataModal(prospectId);
					}}
					className="trigger-popup"
				>
					Detail Analisa
				</a>,
			],
			[
				'Analisa Customer Checking',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerCheckingModal(prospectId);
					}}
				>
					Detail Analisa
				</a>,
			],
		],
		[
			[
				'Dokumen',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();

						const documents: DocumentItem[] = [];
						const getDocumentType = (name: string) => {
							if (name.endsWith('pdf')) return 'pdf';
							if (name.endsWith('jpeg')) return 'jpeg';
							if (name.endsWith('jpg')) return 'jpg';
							if (name.endsWith('png')) return 'png';
						};

						if (customerLegalDocuments && customerLegalDocuments.length) {
							customerLegalDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}

						if (customerBusinessDocuments && customerBusinessDocuments.length) {
							customerBusinessDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}
						if (customerReferralDocuments && customerReferralDocuments.length) {
							customerReferralDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}

						if (documents.length) {
							openDocumentModal(documents);
						} else {
							alert('no document found');
						}
					}}
				>
					View Dokumen
				</a>,
			],
			[
				'SLIK Result',
				mainInformation?.slikResult
					? 'Kol ' + mainInformation?.slikResult
					: '-',
			],
		],
		[
			['FRM Name', mainInformation?.frm.value],
			['CA Name', mainInformation?.creditAnalyst.value],
		],
	];

	const surveyInformationDataRow: DataRowItem[] = [
		[
			[
				'Lokasi Survey Kantor',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openMapModal({
							title: 'Lokasi Survey Kantor',
							address: surveyInformation?.officeAddress,
							latitude: surveyInformation?.officeLat,
							longitude: surveyInformation?.officeLong,
							draggable: false,
							viewOnly: true,
						});
					}}
				>
					{surveyInformation?.officeAddress}
				</a>,
			],
			[
				'Tanggal Survey Kantor',
				dayjs(surveyInformation?.surveyOfficeDate).format('DD-MM-YYYY'),
			],
		],
		[
			[
				'Lokasi Survey Site',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openMapModal({
							title: 'Lokasi Survey Site',
							address: surveyInformation?.siteAddress,
							latitude: surveyInformation?.siteLat,
							longitude: surveyInformation?.siteLong,
							draggable: false,
							viewOnly: true,
						});
					}}
				>
					{surveyInformation?.siteAddress}
				</a>,
			],
			[
				'Tanggal Survey Site',
				dayjs(surveyInformation?.surveySiteDate).format('DD-MM-YYYY'),
			],
		],
	];

	if (isError) return <GenericErrorMessage />;

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<FleetModal
				title={
					<Row
						justify="center"
						gutter={[40, 40]}
					>
						<Col span={24}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
								}}
							>
								<Image src="src/assets/images/check-round-green.png" />
							</div>
						</Col>
						<Col span={24}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
								}}
							>
								<span
									style={{
										fontWeight: 600,
										fontSize: 20,
									}}
								>
									Konfirmasi Penginputan Survey
								</span>
							</div>
						</Col>
						<Col span={24}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
								}}
							>
								<span
									style={{
										fontWeight: 600,
										fontSize: 14,
										color: '#999999',
									}}
								>
									Apakah data yang Anda inputkan sudah benar?
								</span>
							</div>
						</Col>
						<Col />
					</Row>
				}
				open={isOpen}
				onCancel={() => setIsOpen(false)}
				onOk={() => {
					setIsOpen(false);
					form.submit();
				}}
			/>
			<Form
				form={form}
				onFinish={async (values) => {
					try {
						await submitFBDHApprovalSurveyMutation.mutateAsync({
							...values,
							prospectId: mainInformation!.prospectId,
						});
						notification.success({
							message: 'Success',
							description: 'Data has been saved',
						});
						navigate('FBDHRequestSurveyListPage');
					} catch (error) {
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages &&
										message?.metadata?.debugInfo?.messages.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				{/* MAIN INFORMATION */}
				<DataRow
					items={customerInfoDataRow}
					style={{
						marginBottom: 20,
					}}
				/>

				{/* PROSPECT INFORMATION */}
				<FleetTitle>Informasi Limit Pengajuan Pembiayaan Existing</FleetTitle>
				<Table
					scroll={{ x: 'max-content' }}
					pagination={false}
					loading={isLoading}
					bordered
					dataSource={
						prospectInformation?.existingFinancingLimitInformation.map(
							(record, key) => {
								return {
									...record,
									key,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.no,
						},
						{
							title: 'No. MKL',
							render: (_, record) => record.napNo,
						},
						{
							title: 'Limit Pembiayaan Existing',
							render: (_, record) =>
								formatCurrency(record.existingFinancingLimit),
						},
						{
							title: 'Jenis Limit Pembiayaan',
							render: (_, record) => record.financingLimitType,
						},
						{
							title: 'Tipe Limit',
							render: (_, record) => record.financingLimitCharacteristic,
						},
						{
							title: 'Expired Date',
							render: (_, record) =>
								dayjs(record.expiredDate).format('DD/MM/YYYY'),
						},
						{
							title: 'NTF',
							render: (_, record) => formatCurrency(record.ntf) || 0,
						},
						{
							title: 'OS Principal',
							render: (_, record) =>
								formatCurrency(record.outstandingPrincipal),
						},
						{
							title: 'Sisa Limit',
							render: (_, record) =>
								formatCurrency(record.outstandingFinancingLimit),
						},
						{
							title: 'Status',
							render: (_, record) => record.status,
						},
					]}
				/>
				<Divider />
				<FleetTitle>Informasi Limit Pengajuan Pembiayaan</FleetTitle>
				<Table
					bordered
					scroll={{ x: 'max-content' }}
					loading={isLoading}
					pagination={false}
					dataSource={
						prospectInformation?.financingApplicationLimitInformation.map(
							(record, key) => {
								return {
									...record,
									key,
								};
							},
						) ?? []
					}
					columns={[
						{
							title: 'No',
							align: 'center',
							render: (_, record) => record.no,
						},
						{
							title: 'Limit Pembiayaan Existing',
							render: (_, record) =>
								formatCurrency(record.existingFinancingLimit),
						},
						{
							title: 'Jenis Limit Pembiayaan',
							render: (_, record) => record.financingLimitType,
						},
						{
							title: 'OS Principal',
							render: (_, record) =>
								formatCurrency(record.outstandingPrincipal),
						},
						{
							title: 'Tujuan Pembiayaan',
							render: (_, record) => record.financingPurpose,
						},
						{
							title: 'Pengajuan Limit',
							render: (_, record) => formatCurrency(record.limitApplication),
						},
						{
							title: 'Total Pengajuan Plafon',
							render: (_, record) =>
								formatCurrency(record.totalLimitApplication),
						},
						{
							title: 'Segmen Market',
							render: (_, record) => record.segmentMarket,
						},
					]}
				/>
				<Divider />

				{/* Survey Information */}
				<FleetTitle>Survey Information</FleetTitle>
				<DataRow
					items={surveyInformationDataRow}
					style={{
						marginBottom: 20,
					}}
				/>
				<Divider />
				<Table
					bordered
					loading={isLoading}
					pagination={false}
					dataSource={
						surveyParticipants?.map((record, key) => {
							return {
								...record,
								key,
							};
						}) ?? []
					}
					columns={[
						{
							title: (
								<span style={{ display: 'flex', justifyContent: 'center' }}>
									Action
								</span>
							),
							render: (_, _record, index) => {
								return index + 1;
							},
							width: 1,
						},
						{
							title: 'Nama Peserta Survey',
							width: 200,
							align: 'center',
							render: (record) => record.name ?? '-',
						},
						{
							title: 'Jabatan',
							render: (record) => record.position ?? '-',
						},
					]}
				/>
				<Divider />
				{/* Approval History */}
				<FleetTitle>Approval History</FleetTitle>
				<Table
					bordered
					loading={isLoading}
					pagination={false}
					dataSource={
						approvalHistories?.map((approval, key) => {
							return {
								key,
								...approval,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, _record, index) => index + 1,
						},
						{
							title: 'Name',
							render: (_, record) => record.approvalBy,
						},
						{
							title: 'Date',
							render: (_, record) => record.approvalDate,
						},
						{
							title: 'Process',
							render: (_, record) => record.approvalProcess,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							render: (_, record) => record.approvalNote,
						},
					]}
					style={{ marginBottom: 20 }}
				/>

				<Divider />

				<Row gutter={[16, 24]}>
					<Col span={4}>Approval Status</Col>
					<Col span={8}>
						<Form.Item
							name={fieldName('approvalStatus')}
							noStyle
						>
							<Select
								style={{
									width: '100%',
									borderRadius: '4px',
								}}
								options={[
									{
										value: 'Approve',
										label: 'Approve',
									},
									{
										value: 'Return',
										label: 'Return',
									},
								]}
							/>
						</Form.Item>
					</Col>
					<Col span={12} />
				</Row>
				<Divider />
				<Row gutter={[16, 24]}>
					<Col span={4}>Informasi Tambahan</Col>
					<Col span={20}>
						<Form.Item
							noStyle
							name={fieldName('approvalNotes')}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</Col>
				</Row>

				<Button
					type="primary"
					htmlType="submit"
					loading={isLoading || submitFBDHApprovalSurveyMutation.isPending}
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 60,
					}}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default FBDHRequestSurveyPage;
